import React, { useEffect, useRef, useState } from "react";
import { ExternalLink } from "lucide-react";

const OLIVE = "oklch(0.55 0.09 105)";

// Same olive gradient the Liver Biome+ page uses for its gradient headlines
// (ConversionProductPage --twc-headline-fill).
const GRADIENT_TEXT: React.CSSProperties = {
  backgroundImage: "linear-gradient(118deg, #7d8f4a 0%, #56632f 46%, #38411f 78%, #262e14 100%)",
  WebkitBackgroundClip: "text",
  backgroundClip: "text",
  WebkitTextFillColor: "transparent",
  color: "transparent",
};

/** Counts a number from 0 to `end` once the element scrolls into view. */
function CountUp({
  end,
  decimals = 0,
  suffix = "",
  className,
  testId,
  style,
}: {
  end: number;
  decimals?: number;
  suffix?: string;
  className?: string;
  testId?: string;
  style?: React.CSSProperties;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  // Start at the verified final value so server-rendered HTML (and crawlers /
  // no-JS readers) always expose the real research numbers, never 0.
  const [value, setValue] = useState(end);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || started.current) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      setValue(end);
      return;
    }
    const run = () => {
      if (started.current) return;
      started.current = true;
      const duration = 1400;
      const start = performance.now();
      let raf = 0;
      setValue(0);
      const tick = (now: number) => {
        const t = Math.min(1, (now - start) / duration);
        const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
        setValue(end * eased);
        if (t < 1) raf = requestAnimationFrame(tick);
        else setValue(end);
      };
      raf = requestAnimationFrame(tick);
    };
    // If the number is already on screen (first paint), animate right away;
    // otherwise wait until it scrolls into view.
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight * 0.95) {
      run();
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            io.disconnect();
            run();
          }
        }
      },
      { threshold: 0.3 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [end]);

  return (
    <span ref={ref} className={className} data-testid={testId} style={style}>
      {value.toFixed(decimals)}
      {suffix}
    </span>
  );
}

type ResearchCard = {
  stat: number;
  decimals?: number;
  suffix: string;
  statLabel?: string;
  extraStats?: { value: number; decimals?: number; label: string }[];
  text: string;
  link: string;
  linkLabel: string;
};

// Stats and sources supplied by the founder (23 Sep 2026). Education only —
// independent study findings, never product outcome claims.
const CARDS: ResearchCard[] = [
  {
    stat: 65.5,
    decimals: 1,
    suffix: "%",
    text: "of published studies reported a significant drop in elevated liver enzymes when standardised silymarin was used — across 29 randomised controlled trials with 3,846 participants.",
    link: "https://www.researchgate.net/publication/271125450_Effects_of_silymarin_N-acetylcysteine_and_selenium_in_the_treatment_of_papulopustular_acne",
    linkLabel: "Silymarin, NAC & selenium review",
  },
  {
    stat: 78,
    suffix: "%",
    extraStats: [
      { value: 42, label: "AST" },
      { value: 40, label: "ALT" },
      { value: 34, label: "GGT" },
    ],
    text: "of patients in a fatty-liver clinical trial saw a meaningful reduction in overall liver enzymes, with targeted drops in AST, ALT and GGT.",
    link: "https://www.jimc.ir/article_138254.html",
    linkLabel: "Clinical trial · Silymarin in fatty liver",
  },
  {
    stat: 64.5,
    decimals: 1,
    suffix: "%",
    extraStats: [{ value: 45.9, decimals: 1, label: "AST" }],
    text: "improvement in ALT, and 45.9% in AST, when silymarin and NAC acted together under severe toxic liver stress — versus no protection.",
    link: "https://pmc.ncbi.nlm.nih.gov/articles/PMC5051100/",
    linkLabel: "Silymarin vs NAC · Toxic liver injury",
  },
  {
    stat: 600,
    suffix: " mg",
    statLabel: "NAC · twice daily",
    text: "was the dose used in a 12-week human trial where NAC significantly reduced serum ALT levels in people with fatty liver changes.",
    link: "https://pmc.ncbi.nlm.nih.gov/articles/PMC3270338/",
    linkLabel: "NAC in fatty liver · 12-week trial",
  },
];

// Absorption multiples supplied by the founder (23 Sep 2026), matching the
// campaign creative. Education only.
const ABSORB_CARDS: ResearchCard[] = [
  {
    stat: 9.6,
    decimals: 1,
    suffix: "×",
    statLabel: "Liposomal Curcumin",
    text: "Better absorption than regular curcumin.",
    link: "",
    linkLabel: "",
  },
  {
    stat: 53,
    suffix: "×",
    statLabel: "Turmeric powder",
    text: "Higher cellular absorption versus regular turmeric powder.",
    link: "",
    linkLabel: "",
  },
  {
    stat: 3,
    suffix: "×",
    statLabel: "Liposomal ALA",
    text: "Better absorption than standard ALA.",
    link: "",
    linkLabel: "",
  },
];

function StatLine({
  value,
  decimals = 0,
  label,
  size = "md",
}: {
  value: number;
  decimals?: number;
  label: string;
  size?: "md" | "lg";
}) {
  return (
    <div className="flex items-baseline gap-1.5">
      <CountUp
        end={value}
        decimals={decimals}
        suffix="%"
        className="text-xl font-bold tabular-nums"
        style={{ color: OLIVE }}
      />
      <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-foreground/60">
        {label}
      </span>
    </div>
  );
}

function Card({ card }: { card: ResearchCard }) {
  return (
    <div className="flex h-full flex-col rounded-2xl border border-border bg-card p-6">
      <div className="flex items-baseline gap-2">
      <CountUp
        end={card.stat}
        decimals={card.decimals ?? 0}
        suffix={card.suffix}
        testId={`research-stat-${card.stat}`}
        className="font-display text-4xl font-bold tracking-tight tabular-nums md:text-5xl"
        style={GRADIENT_TEXT}
      />
        {card.statLabel ? (
          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-foreground/60">
            {card.statLabel}
          </span>
        ) : null}
      </div>

      {card.extraStats ? (
        <div className="mt-3 flex gap-5">
          {card.extraStats.map((s) => (
            <StatLine key={s.label} value={s.value} decimals={s.decimals} label={s.label} />
          ))}
        </div>
      ) : null}

      <p className="mt-3 text-[13.5px] leading-relaxed text-foreground!">{card.text}</p>

      {card.link ? (
        <a
          href={card.link}
          target="_blank"
          rel="noreferrer"
          className="mt-auto inline-flex items-center gap-1.5 pt-4 text-[12px] font-semibold text-foreground underline decoration-border underline-offset-4 transition-colors hover:decoration-foreground"
        >
          <span>{card.linkLabel}</span>
          <ExternalLink className="h-3 w-3 flex-shrink-0" style={{ color: OLIVE }} />
        </a>
      ) : null}
    </div>
  );
}

/** Horizontal card deck: full-width cards snap sideways; non-centred cards
 *  scale down and dim, so the incoming card sweeps over the previous one. */
function DeckSlider({ children }: { children: React.ReactNode }) {
  const trackRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  const onScroll = () => {
    const track = trackRef.current;
    if (!track) return;
    const center = track.scrollLeft + track.clientWidth / 2;
    itemRefs.current.forEach((item) => {
      if (!item) return;
      const mid = item.offsetLeft + item.offsetWidth / 2;
      const dist = Math.min(1, Math.abs(center - mid) / item.offsetWidth);
      // dist 0 = centred card, 1 = fully off to the side
      const scale = 1 - dist * 0.12;
      const opacity = 1 - dist * 0.55;
      item.style.transform = `scale(${scale})`;
      item.style.opacity = String(opacity);
      item.style.zIndex = String(Math.round((1 - dist) * 10));
    });
  };

  useEffect(() => {
    onScroll();
    const track = trackRef.current;
    if (track) {
      track.addEventListener("scroll", onScroll, { passive: true });
      return () => track.removeEventListener("scroll", onScroll);
    }
  }, []);

  return (
    <div
      ref={trackRef}
      onScroll={onScroll}
      className="deck-slider -mx-4 flex snap-x snap-mandatory gap-0 overflow-x-auto px-4 pb-2"
    >
      {React.Children.map(children, (child, i) => (
        <div
          key={i}
          ref={(el) => {
            itemRefs.current[i] = el;
          }}
          className="w-[90%] flex-shrink-0 snap-center pr-1 transition-[transform,opacity] duration-150 ease-out will-change-transform"
          style={{ scrollSnapStop: "always" }}
        >
          {child}
        </div>
      ))}
    </div>
  );
}

export function LiverResearchCards() {
  return (
    <section
      aria-labelledby="liver-research-heading"
      className="mt-10 md:mt-16"
    >
      <div className="mx-auto max-w-3xl">
        <p
          className="text-center text-[11px] font-semibold uppercase tracking-[0.22em]"
          style={{ color: OLIVE }}
        >
          Published Research
        </p>
        <h2
          id="liver-research-heading"
          className="mt-2 text-center font-display text-3xl font-bold text-foreground md:text-4xl"
        >
          What the studies show
        </h2>

        {/* Mobile: horizontal deck slider — the next card slides over the
            previous one. Desktop: 2-column grid. */}
        <div className="mt-8 md:hidden">
          <DeckSlider>
            {CARDS.map((card) => (
              <Card key={card.link} card={card} />
            ))}
          </DeckSlider>
        </div>
        <div className="mt-8 hidden gap-4 sm:grid sm:grid-cols-2">
          {CARDS.map((card) => (
            <Card key={card.link} card={card} />
          ))}
        </div>

        <p className="mt-5 text-center text-[11px] leading-relaxed text-foreground/60">
          Findings from independent published research on silymarin and NAC,
          shared for education. They describe the studied compounds, not
          guaranteed results from any supplement, and are not medical advice.
        </p>
      </div>

      {/* Absorption cards (founder-supplied stats, 23 Sep 2026) */}
      <div className="mx-auto mt-12 max-w-3xl">
        <DeckSlider>
          {ABSORB_CARDS.map((card) => (
            <Card key={card.statLabel} card={{ ...card, link: "", linkLabel: "" }} />
          ))}
        </DeckSlider>
        <div className="mt-6 hidden gap-4 sm:grid sm:grid-cols-3">
          {ABSORB_CARDS.map((card) => (
            <Card key={card.statLabel} card={{ ...card, link: "", linkLabel: "" }} />
          ))}
        </div>
        <p className="mt-6 text-center font-display text-lg font-semibold text-foreground md:text-xl">
          You don't need more. You need a better form.
        </p>
      </div>
    </section>
  );
}
